import React, { useState, useEffect, useCallback } from 'react';
import { UserX, UserCheck, AlertTriangle, Clock, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { API } from '../../App';

const card = 'bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-4';
const input = 'w-full bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] font-mono focus:border-[#FF4D00] focus:outline-none min-h-[44px]';
const btnPrimary = 'px-4 py-2 min-h-[44px] bg-[#FF4D00] text-[#0A0A0A] font-bold text-sm rounded hover:bg-[#E63E00] transition-colors';
const btnDanger = 'px-4 py-2 min-h-[44px] bg-[#A61E17] text-white font-bold text-sm rounded hover:bg-[#8B1912] transition-colors';
const btnSecondary = 'px-4 py-2 min-h-[44px] border border-[#2A2A2A] text-[#A0A0A0] text-sm rounded hover:border-[#FF4D00] hover:text-[#F5F5F5] transition-colors';

export default function SuspensionSection({ workspaceId }) {
  const [suspended, setSuspended] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSuspendForm, setShowSuspendForm] = useState(false);
  const [suspendEmail, setSuspendEmail] = useState('');
  const [suspendReason, setSuspendReason] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  const [history, setHistory] = useState({});
  const [actionLoading, setActionLoading] = useState(null);
  const [showOffboard, setShowOffboard] = useState(null);
  const [offboardReason, setOffboardReason] = useState('');

  const fetchSuspended = useCallback(async () => {
    try {
      const r = await API.get('/api/admin/users/suspended', { params: { workspace_id: workspaceId } });
      setSuspended(r.data.users || []);
    } catch { setSuspended([]); }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { fetchSuspended(); }, [fetchSuspended]);

  const fetchHistory = async (userId) => {
    if (history[userId]) return;
    try {
      const r = await API.get(`/api/admin/users/${userId}/suspension-history`);
      setHistory(h => ({ ...h, [userId]: r.data.history || [] }));
    } catch { setHistory(h => ({ ...h, [userId]: [] })); }
  };

  const handleSuspend = async () => {
    if (!suspendEmail) return;
    setActionLoading('suspend');
    try {
      await API.post('/api/admin/users/suspend', { email: suspendEmail, reason: suspendReason, workspace_id: workspaceId });
      setSuspendEmail(''); setSuspendReason(''); setShowSuspendForm(false);
      fetchSuspended();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to suspend user'); }
    setActionLoading(null);
  };

  const handleReactivate = async (userId) => {
    setActionLoading(userId);
    try {
      await API.post('/api/admin/users/reactivate', { user_id: userId, workspace_id: workspaceId });
      fetchSuspended();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to reactivate'); }
    setActionLoading(null);
  };

  const handleOffboard = async (userId) => {
    setActionLoading(userId);
    try {
      await API.post('/api/admin/users/offboard', { user_id: userId, reason: offboardReason, workspace_id: workspaceId });
      setShowOffboard(null); setOffboardReason('');
      fetchSuspended();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to offboard'); }
    setActionLoading(null);
  };

  if (loading) return <div className="text-center py-10 text-[#A0A0A0]">Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold font-['Barlow_Condensed'] text-[#F5F1E8]">User Suspension & Offboarding</h2>
        <button onClick={() => setShowSuspendForm(!showSuspendForm)} className={btnPrimary}>
          <UserX className="w-4 h-4 inline mr-1" />Suspend User
        </button>
      </div>

      {showSuspendForm && (
        <div className={card}>
          <h3 className="text-sm font-mono text-[#F2A900] mb-3">Suspend a User</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-mono text-[#A0A0A0] mb-1">Email</label>
              <input value={suspendEmail} onChange={e => setSuspendEmail(e.target.value)} placeholder="user@example.com" className={input} />
            </div>
            <div>
              <label className="block text-xs font-mono text-[#A0A0A0] mb-1">Reason</label>
              <input value={suspendReason} onChange={e => setSuspendReason(e.target.value)} placeholder="Policy violation..." className={input} />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={handleSuspend} disabled={actionLoading === 'suspend'} className={btnDanger}>
              {actionLoading === 'suspend' ? 'Suspending...' : 'Confirm Suspend'}
            </button>
            <button onClick={() => setShowSuspendForm(false)} className={btnSecondary}>Cancel</button>
          </div>
        </div>
      )}

      {suspended.length === 0 ? (
        <div className={`${card} text-center`}>
          <UserCheck className="w-8 h-8 mx-auto mb-2 text-[#00D4FF] opacity-50" />
          <p className="text-sm text-[#A0A0A0]">No suspended users</p>
        </div>
      ) : (
        <div className="space-y-2">
          {suspended.map(u => (
            <div key={u.id} className={card}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <UserX className="w-4 h-4 text-[#A61E17]" />
                  <div>
                    <span className="text-sm text-[#F5F1E8] font-mono">{u.email || u.name}</span>
                    {u.suspended_at && <span className="text-xs text-[#A0A0A0] ml-2">since {new Date(u.suspended_at).toLocaleDateString()}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleReactivate(u.id || u.user_id)} disabled={actionLoading === (u.id || u.user_id)} className={btnSecondary}>
                    <UserCheck className="w-3.5 h-3.5 inline mr-1" />Reactivate
                  </button>
                  <button onClick={() => setShowOffboard(showOffboard === u.id ? null : u.id)} className={btnDanger}>
                    Offboard
                  </button>
                  <button onClick={() => { const uid = u.id || u.user_id; setExpandedUser(expandedUser === uid ? null : uid); fetchHistory(uid); }}>
                    {expandedUser === (u.id || u.user_id) ? <ChevronUp className="w-4 h-4 text-[#A0A0A0]" /> : <ChevronDown className="w-4 h-4 text-[#A0A0A0]" />}
                  </button>
                </div>
              </div>
              {u.reason && <p className="text-xs text-[#A0A0A0] mt-1 ml-7">Reason: {u.reason}</p>}

              {showOffboard === u.id && (
                <div className="mt-3 p-3 bg-[#0F0F0F] rounded border border-[#A61E17]/30">
                  <p className="text-xs text-[#A61E17] font-mono mb-2"><AlertTriangle className="w-3 h-3 inline mr-1" />This permanently removes the user and their data</p>
                  <input value={offboardReason} onChange={e => setOffboardReason(e.target.value)} placeholder="Offboarding reason..." className={input} />
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => handleOffboard(u.id || u.user_id)} disabled={actionLoading === (u.id || u.user_id)} className={btnDanger}>Confirm Offboard</button>
                    <button onClick={() => setShowOffboard(null)} className={btnSecondary}>Cancel</button>
                  </div>
                </div>
              )}

              {expandedUser === (u.id || u.user_id) && (
                <div className="mt-3 ml-7 space-y-1">
                  <p className="text-xs font-mono text-[#F2A900] mb-1">Suspension History</p>
                  {(history[u.id || u.user_id] || []).length === 0 ? (
                    <p className="text-xs text-[#A0A0A0]">No history</p>
                  ) : (
                    (history[u.id || u.user_id] || []).map((h, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-[#A0A0A0]">
                        <Clock className="w-3 h-3" />
                        <span className="font-mono">{new Date(h.created_at).toLocaleString()}</span>
                        <span className={h.action === 'suspended' ? 'text-[#A61E17]' : 'text-[#00D4FF]'}>{h.action}</span>
                        {h.reason && <span>— {h.reason}</span>}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
